// QuinCardSection.jsx
import React, { useState } from "react";
import {
  Paper,
  Box,
  IconButton,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  CircularProgress,
  TextField,
} from "@mui/material";
import { ExpandMore, ExpandLess, Close } from "@mui/icons-material";
import MyTitle from "../../components/CardSection/MyTitle";
import QuinInputNum from "./QuinInputNum";
import MyButton from "../../components/CardSection/MyButton";
import { functions } from "../../config/firebaseConfig";
import { httpsCallable } from "firebase/functions";

const QuinCardSection = ({
  title,
  myStore,
  listItem = [],
  handleSelectedItemChange,
  selectedItem,
  handleChangeValue,
  handleAdd,
  selectedValue,
  onClick,
  children,
  showDefaultControls = true,
  showPrice = true,
  open: controlledOpen,
  setOpen: setControlledOpen,
  nameValue = "",
  onNameChange,
}) => {
  const [showComponents, setShowComponents] = useState(false);
  const [searchNumber, setSearchNumber] = useState("");
  const [resultData, setResultData] = useState("");
  const [cartItems, setCartItems] = useState([]);
  const [quantity, setQuantity] = useState("");
  const [searching, setSearching] = useState(false);
  const isControlled =
    controlledOpen !== undefined && setControlledOpen !== undefined;
  const open = isControlled ? controlledOpen : showComponents;

  const handleSearchNumberChange = (event) => {
    setSearchNumber(event.target.value);
    if (!event.target.value) {
      setResultData("");
    }
  };

  const parsedResult = React.useMemo(() => {
    if (!resultData) return null;
    const lastUnderscore = resultData.lastIndexOf("_");
    if (lastUnderscore === -1) return { name: resultData, amount: null };
    const name = resultData.substring(0, lastUnderscore);
    const rawValue = resultData.substring(lastUnderscore + 1);
    const numValue = parseFloat(rawValue.replace(",", "."));
    if (isNaN(numValue)) return { name: resultData, amount: null };
    return { name, amount: numValue };
  }, [resultData]);

  const handleAddToCart = () => {
    if (!parsedResult || parsedResult.amount == null) return;
    const qte = parseInt(quantity, 10) || 1;
    setCartItems((prev) => {
      const existing = prev.find((item) => item.id === searchNumber);
      if (existing) {
        const newQte = existing.qte + qte;
        return prev.map((item) =>
          item.id === searchNumber
            ? { ...item, qte: newQte, tot: item.prix * newQte }
            : item,
        );
      }
      return [
        ...prev,
        {
          id: searchNumber,
          article: parsedResult.name,
          prix: parsedResult.amount,
          qte,
          tot: parsedResult.amount * qte,
        },
      ];
    });
    setSearchNumber("");
    setResultData("");
    setQuantity("");
  };

  const handleRemoveFromCart = (id) => {
    setCartItems((prev) => prev.filter((item) => item.id !== id));
  };

  const handleSearch = async () => {
    if (!searchNumber) return;
    setSearching(true);
    setResultData("");
    try {
      const lookup = httpsCallable(functions, "quinLookup");
      const result = await lookup({ id: searchNumber });
      setResultData(String(result.data.value ?? ""));
    } catch (e) {
      console.error("Error searching quinLookup:", e);
      setResultData(
        e?.code === "functions/not-found"
          ? "Aucun r\u00e9sultat"
          : "Erreur de recherche",
      );
    } finally {
      setSearching(false);
    }
  };

  const handleCardClick = () => {
    if (isControlled) {
      setControlledOpen(!controlledOpen);
    } else {
      setShowComponents((prev) => !prev);
    }
  };

  return (
    <Paper
      elevation={0}
      variant="outlined"
      role="button"
      tabIndex={0}
      aria-expanded={open}
      aria-label={`Toggle ${title} section`}
      sx={{
        display: "flex",
        flexDirection: "column",
        alignItems: "stretch",
        gap: 0.5,
        mt: 2,
        p: 1,
      }}
    >
      <Box
        onClick={handleCardClick}
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          cursor: "pointer",
          "&:hover": {
            backgroundColor: "action.hover",
          },
          borderRadius: 1,
          p: 0.5,
        }}
      >
        <MyTitle text={title} />
        <IconButton
          size="small"
          onClick={(e) => {
            e.stopPropagation();
            handleCardClick();
          }}
          sx={{ ml: 1 }}
        >
          {showComponents ? <ExpandLess /> : <ExpandMore />}
        </IconButton>
      </Box>

      {open && (
        <Box
          onClick={(e) => e.stopPropagation()}
          sx={{
            display: "flex",
            flexDirection: "column",
            gap: 1,
            mt: 0.5,
            width: "auto",
            maxWidth: "100%",
            px: 1.5,
            pb: 1,
          }}
        >
          {children}

          {showDefaultControls && (
            <>
              <QuinInputNum
                disabled={searching}
                selectedValue={searchNumber}
                handleChangeValue={handleSearchNumberChange}
                handleAdd={handleSearch}
                buttonLabel="Rechercher"
                buttonDisabled={!searchNumber || searching}
              />
              <Box display="flex" alignItems="flex-start" gap={1} width="100%">
                <Box flex={2}>
                  <Typography
                    variant="body1"
                    component="div"
                    sx={{
                      whiteSpace: "pre-wrap",
                      wordBreak: "break-word",
                      minHeight: 40,
                      p: 1,
                      border: 1,
                      borderColor: "divider",
                      borderRadius: 1,
                      display: "flex",
                      flexDirection: "column",
                      alignItems: searching ? "center" : "flex-start",
                      justifyContent: searching ? "center" : "flex-start",
                    }}
                  >
                    {searching ? (
                      <CircularProgress size={24} />
                    ) : (
                      parsedResult && (
                        <>
                          <div>{parsedResult.name}</div>
                          {showPrice && parsedResult.amount != null && (
                            <div>
                              {parsedResult.amount.toLocaleString("fr-FR", {
                                minimumFractionDigits: 0,
                                maximumFractionDigits: 0,
                              })}{" "}
                              CDF
                            </div>
                          )}
                        </>
                      )
                    )}
                  </Typography>
                </Box>
              </Box>
              <QuinInputNum
                disabled={!resultData}
                selectedValue={quantity}
                handleChangeValue={(e) => setQuantity(e.target.value)}
                handleAdd={handleAddToCart}
                buttonLabel="+"
                buttonDisabled={!resultData || !quantity}
                placeholder="Quantité"
              />

              {onNameChange && (
                <TextField
                  size="small"
                  fullWidth
                  label="Votre nom"
                  value={nameValue}
                  onChange={onNameChange}
                  onClick={(e) => e.stopPropagation()}
                  disabled={cartItems.length === 0}
                />
              )}

              {cartItems.length > 0 && (
                <Box sx={{ mt: 1 }}>
                  <TableContainer component={Paper} variant="outlined">
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: "bold", px: 1 }}>
                            ID
                          </TableCell>
                          <TableCell sx={{ fontWeight: "bold", px: 1 }}>
                            ARTICLE
                          </TableCell>
                          {showPrice && (
                            <TableCell
                              sx={{ fontWeight: "bold", px: 1 }}
                              align="right"
                            >
                              PRIX
                            </TableCell>
                          )}
                          <TableCell
                            sx={{ fontWeight: "bold", px: 1 }}
                            align="center"
                          >
                            QTE
                          </TableCell>
                          {showPrice && (
                            <TableCell
                              sx={{ fontWeight: "bold", px: 1 }}
                              align="right"
                            >
                              TOT.
                            </TableCell>
                          )}
                          <TableCell sx={{ px: 0.5 }} />
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {cartItems.map((row) => (
                          <TableRow key={row.id}>
                            <TableCell sx={{ px: 1 }}>{row.id}</TableCell>
                            <TableCell sx={{ px: 1 }}>{row.article}</TableCell>
                            {showPrice && (
                              <TableCell sx={{ px: 1 }} align="right">
                                {row.prix.toLocaleString("fr-FR")}
                              </TableCell>
                            )}
                            <TableCell sx={{ px: 1 }} align="center">
                              {row.qte}
                            </TableCell>
                            {showPrice && (
                              <TableCell sx={{ px: 1 }} align="right">
                                {row.tot.toLocaleString("fr-FR")}
                              </TableCell>
                            )}
                            <TableCell sx={{ px: 0.5 }}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => handleRemoveFromCart(row.id)}
                              >
                                <Close fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  {showPrice && (
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: "bold", textAlign: "right", mt: 1 }}
                    >
                      {cartItems
                        .reduce((sum, item) => sum + item.tot, 0)
                        .toLocaleString("fr-FR")}{" "}
                      CDF
                    </Typography>
                  )}
                  <MyButton
                    disabled={onNameChange ? !nameValue.trim() : false}
                    onClick={(e) => {
                      onClick(e, cartItems);
                      setCartItems([]);
                    }}
                  />
                </Box>
              )}
            </>
          )}
        </Box>
      )}
    </Paper>
  );
};

export default QuinCardSection;
